import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin, 
  Sparkles,
  Heart,
  ArrowRight,
  Settings,
  Building,
  Award,
  Star,
  Zap
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '/', icon: ArrowRight },
    { name: 'Products', href: '/products', icon: ArrowRight },
    { name: 'Blogs', href: '/blogs', icon: ArrowRight },
    { name: 'Contact', href: '/contact', icon: ArrowRight },
    { name: 'About Us', href: '/about', icon: ArrowRight }
  ];

  const productCategories = [
    { name: 'GI Door Frames', href: '/products?category=door-frames', icon: Building },
    { name: 'GI Window Frames', href: '/products?category=window-frames', icon: Building },
    { name: 'Ventilation Systems', href: '/products?category=ventilation', icon: Settings },
    { name: 'Custom Solutions', href: '/contact', icon: Zap }
  ];

  const socialLinks = [
    { name: 'Facebook', href: '#', icon: Facebook, color: 'hover:text-blue-600' },
    { name: 'Twitter', href: '#', icon: Twitter, color: 'hover:text-blue-400' },
    { name: 'Instagram', href: '#', icon: Instagram, color: 'hover:text-pink-600' },
    { name: 'LinkedIn', href: '#', icon: Linkedin, color: 'hover:text-blue-700' }
  ];

  return (
    <footer className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 overflow-hidden">
      {/* Subtle Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-indigo-600/5"></div>
        <div className="absolute top-0 left-1/3 w-32 h-32 bg-blue-500/5 rounded-full blur-2xl"></div>
        <div className="absolute bottom-0 right-1/3 w-24 h-24 bg-indigo-500/5 rounded-full blur-xl"></div>
      </div>

      {/* Top Border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Main Content - Compact Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-6">
          
          {/* Company Info - Condensed */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center group mb-4">
              <div className="bg-white/10 backdrop-blur-sm p-2 rounded-full border border-white/20">
                <img src="/images/logo.png" alt="Happy Steels Logo" className="w-8 h-8 object-contain" />
              </div>
              <div className="ml-3">
                <h2 className="text-lg font-bold text-white flex items-center">
                  HAPPY STEELS
                  <Sparkles className="w-4 h-4 ml-2 text-blue-400" />
                </h2>
                <div className="text-blue-200 text-xs">Premium GI Solutions</div>
              </div>
            </Link>
            
            <p className="text-gray-300 text-sm leading-relaxed mb-4 max-w-sm">
              India's trusted manufacturer of premium GI door frames, window systems, and ventilation solutions.
            </p>

            {/* Compact Stats */}
            <div className="flex space-x-4 mb-4">
              <div className="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 px-3 py-2 text-center">
                <div className="text-lg font-bold text-white">25+</div>
                <div className="text-xs text-gray-300">Years</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 px-3 py-2 text-center">
                <div className="text-lg font-bold text-white">15K+</div>
                <div className="text-xs text-gray-300">Clients</div>
              </div>
            </div>

            {/* Social Links - Smaller */}
            <div className="flex space-x-2">
              {socialLinks.map((social) => (
                <a 
                  key={social.name}
                  href={social.href} 
                  className={`bg-white/10 backdrop-blur-sm border border-white/20 p-2 rounded-full text-gray-300 hover:bg-white/20 ${social.color} transition-all duration-300`}
                  aria-label={social.name}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links - Condensed */}
          <div>
            <h3 className="text-white text-sm font-semibold mb-3 flex items-center">
              <Sparkles className="h-3 w-3 mr-2 text-blue-400" />
              Quick Links
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-gray-300 hover:text-white transition-colors text-sm flex items-center group"
                  >
                    <link.icon className="h-3 w-3 mr-2 text-blue-400 group-hover:translate-x-1 transition-transform" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products - Condensed */}
          <div>
            <h3 className="text-white text-sm font-semibold mb-3 flex items-center">
              <Building className="h-3 w-3 mr-2 text-blue-400" />
              Products
            </h3>
            <ul className="space-y-2">
              {productCategories.slice(0, 3).map((category) => (
                <li key={category.name}>
                  <Link 
                    to={category.href} 
                    className="text-gray-300 hover:text-white transition-colors text-sm flex items-center group"
                  >
                    <category.icon className="h-3 w-3 mr-2 text-blue-400 group-hover:scale-110 transition-transform" />
                    {category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact & Team - Single Row */}
        <div className="border-t border-white/20 pt-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-center">
            
            {/* Contact Info - Inline */}
            <div className="flex flex-wrap gap-4 text-xs text-gray-300">
              <div className="flex items-center space-x-2">
                <Phone className="w-3 h-3 text-blue-400" />
                <span>+91 9919100155</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-3 h-3 text-blue-400" />
                <span>akyenterprises@happysteels.in</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="w-3 h-3 text-blue-400" />
                <span>Amethi, UP</span>
              </div>
            </div>

            {/* Team - Ultra Compact */}
            <div className="flex items-center justify-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-full overflow-hidden border border-blue-400/40">
                  <img src="/images/manager-vikas.jpg" alt="Mr. Vikas" className="w-full h-full object-cover" />
                </div>
                <div className="text-xs">
                  <div className="text-white font-medium">Mr. Vikas</div>
                  <div className="text-blue-200">Manager</div>
                </div>
              </div>
              <div className="w-px h-6 bg-white/20"></div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-full overflow-hidden border border-indigo-400/40">
                  <img src="/images/developer-arpit.jpg" alt="Mr. Arpit" className="w-full h-full object-cover" />
                </div>
                <div className="text-xs">
                  <div className="text-white font-medium">Mr. Arpit</div>
                  <div className="text-indigo-200">Developer</div>
                </div>
              </div>
            </div>


          </div>
        </div>

        {/* Bottom Section - Single Line */}
        <div className="border-t border-white/20 pt-4">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
            
            {/* Copyright */}
            <div className="text-gray-300 text-xs flex items-center">
              <span>© {currentYear} Happy Steels. All rights reserved.</span>
              <Heart className="w-3 h-3 mx-2 text-red-500" />
              <span>Made in India</span>
            </div>

            {/* Quality Badges - Compact */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1 text-gray-300 text-xs">
                <Award className="w-3 h-3 text-yellow-500" />
                <span>ISI Certified</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-300 text-xs">
                <Star className="w-3 h-3 text-yellow-500" />
                <span>Quality Assured</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
